import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import {
  useInfiniteItemsListLoader,
  useRequestQueue,
} from '../../../../../hooks'
import { getItemsFromIdwellApiResponse } from '../../../../../utils/api'
import {
  getRequestBoardColumnCount,
  getRequestBoardColumnItems,
} from '../../RequestBoard.api'
import {
  REQUEST_BOARD_COLUMN_PAGE_SIZE,
  REQUEST_BOARD_ORDERING,
} from '../../RequestBoard.constants'
import { removeRequestBoardId } from '../../RequestBoard.utils'

import {
  REQUEST_BOARD_COLUMN_INITIAL_COUNT,
  REQUEST_BOARD_COLUMN_INITIAL_PAGE_OFFSET,
  REQUEST_BOARD_COLUMN_OBSERVER_OPTIONS,
  REQUEST_BOARD_COLUMN_THRESHOLD_END_INDEX,
} from './useRequestBoardColumn.constants'
import {
  checkRequestBoardColumnIsLastPage,
  getRequestBoardColumnFilters,
  getRequestBoardColumnFirstPageParams,
  getRequestBoardColumnNextPageParams,
  getRequestBoardColumnSyncCountChange,
  getRequestBoardColumnVisibleItems,
  insertRequestBoardColumnItem,
  isRequestBoardColumnCountCurrent,
  isRequestBoardColumnExcluded,
  isRequestBoardColumnListStale,
  isRequestBoardColumnLoadMorePaused,
  isRequestBoardColumnPageExhausted,
  isRequestBoardColumnReloaded,
  isRequestBoardColumnUpdateNeeded,
  mergeRequestBoardColumnItems,
  removeRequestBoardColumnItem,
  syncRequestBoardColumnItem,
  updateRequestBoardColumnItem,
} from './useRequestBoardColumn.utils'

export function useRequestBoardColumn({
  filterValues,
  isEnabled,
  status,
  onLoadPage,
}) {
  const [count, setCount] = useState(REQUEST_BOARD_COLUMN_INITIAL_COUNT)
  const [countFilters, setCountFilters] = useState(null)
  const [hasCountError, setCountError] = useState(false)
  const [departedIds, setDepartedIds] = useState(EMPTY_LIST)
  const [insertedIds, setInsertedIds] = useState(EMPTY_LIST)

  const reloadGenerationRef = useRef(0)
  const pageGenerationRef = useRef(0)
  const countVersionRef = useRef(0)
  const countRequestIdRef = useRef(0)
  const listFiltersRef = useRef(null)
  const isListHiddenRef = useRef(false)
  const pageOffsetRef = useRef(REQUEST_BOARD_COLUMN_INITIAL_PAGE_OFFSET)
  const requestedItemsRef = useRef(EMPTY_LIST)

  const { enqueue } = useRequestQueue()

  const isExcluded = isRequestBoardColumnExcluded({ filterValues, status })

  const loadPermission = isEnabled && !isExcluded

  const filters = getRequestBoardColumnFilters({ filterValues, status })

  function getNextPageParams(loadedItems) {
    requestedItemsRef.current = loadedItems

    return getRequestBoardColumnNextPageParams({
      items: loadedItems,
      pageOffset: pageOffsetRef.current,
    })
  }

  function handleChangeInitialized(isInitialized) {
    if (isInitialized) {
      pageOffsetRef.current = REQUEST_BOARD_COLUMN_INITIAL_PAGE_OFFSET
      requestedItemsRef.current = EMPTY_LIST
    }
  }

  function handleLoadPage(response) {
    const pageItems = getItemsFromIdwellApiResponse(response)

    pageGenerationRef.current += 1

    if (
      isRequestBoardColumnPageExhausted({
        items: requestedItemsRef.current,
        pageItems,
      })
    ) {
      pageOffsetRef.current += 1
    }

    onLoadPage(pageItems)
  }

  const {
    getThresholdItemProps,
    hasError,
    items,
    loadingInitial,
    loadingMore,
    mutateItems,
    updateListData,
  } = useInfiniteItemsListLoader({
    apiParams: {
      filters,
      ordering: REQUEST_BOARD_ORDERING,
      size: REQUEST_BOARD_COLUMN_PAGE_SIZE,
    },
    checkIsLastPage: checkRequestBoardColumnIsLastPage,
    getApiParamsInit: getRequestBoardColumnFirstPageParams,
    getApiParamsLoadNext: getNextPageParams,
    getApiParamsUpdate: getRequestBoardColumnFirstPageParams,
    getData: getRequestBoardColumnItems,
    getDataItems: getItemsFromIdwellApiResponse,
    getMergedItems: mergeRequestBoardColumnItems,
    loadPermission,
    observerOptions: REQUEST_BOARD_COLUMN_OBSERVER_OPTIONS,
    setAdditionalData: handleLoadPage,
    thresholdEndIndex: REQUEST_BOARD_COLUMN_THRESHOLD_END_INDEX,
    onChangeInitialized: handleChangeInitialized,
  })

  async function requestCount(countRequestId) {
    return countRequestId === countRequestIdRef.current
      ? getRequestBoardColumnCount(filters).then(({ data }) => data)
      : null
  }

  // Only the latest count applies, and a superseded one is not sent at all.
  async function loadCount() {
    countRequestIdRef.current += 1

    const countRequestId = countRequestIdRef.current
    const countVersion = countVersionRef.current

    return enqueue(() => requestCount(countRequestId))
      .then(data => {
        if (
          countRequestId === countRequestIdRef.current &&
          countVersion === countVersionRef.current
        ) {
          setCount(data.total)
          setCountFilters(filters)
          setCountError(false)
        }

        return true
      })
      .catch(() => {
        if (
          countRequestId === countRequestIdRef.current &&
          countVersion === countVersionRef.current
        ) {
          setCountFilters(filters)
          setCountError(true)
        }

        return false
      })
  }

  function changeCount(updater) {
    countVersionRef.current += 1

    setCount(updater)
  }

  useEffect(() => {
    if (loadPermission) {
      if (
        isRequestBoardColumnUpdateNeeded({
          filters,
          isListHidden: isListHiddenRef.current,
          listFilters: listFiltersRef.current,
        })
      ) {
        reloadGenerationRef.current += 1

        loadCount()

        if (
          isRequestBoardColumnListStale({
            filters,
            isListHidden: isListHiddenRef.current,
            listFilters: listFiltersRef.current,
          })
        ) {
          updateListData()
        }
      }

      isListHiddenRef.current = false
      listFiltersRef.current = filters
    } else {
      isListHiddenRef.current = true
    }
  }, [filters, loadPermission])

  function mutateColumnItems(updater) {
    pageOffsetRef.current = REQUEST_BOARD_COLUMN_INITIAL_PAGE_OFFSET
    requestedItemsRef.current = EMPTY_LIST

    mutateItems(updater)
  }

  function getThresholdCardProps(item) {
    return isRequestBoardColumnLoadMorePaused({ departedIds, insertedIds })
      ? EMPTY_OBJECT
      : getThresholdItemProps((items ?? EMPTY_LIST).indexOf(item))
  }

  async function handleReload() {
    if (loadPermission) {
      reloadGenerationRef.current += 1

      return Promise.all([updateListData(), loadCount()]).then(
        isRequestBoardColumnReloaded
      )
    }
  }

  function handleReloadCount() {
    if (loadPermission) {
      loadCount()
    }
  }

  function handleInsertItem({ index, item }) {
    setInsertedIds(prevIds => [...prevIds, item.id])

    mutateColumnItems(prevItems =>
      insertRequestBoardColumnItem({ index, item, items: prevItems })
    )

    changeCount(prevCount => prevCount + 1)
  }

  function handleSettleInsertedItem(id) {
    setInsertedIds(prevIds => removeRequestBoardId({ id, ids: prevIds }))
  }

  function handleRemoveItem(id) {
    mutateColumnItems(prevItems =>
      removeRequestBoardColumnItem({ id, items: prevItems })
    )

    changeCount(prevCount => prevCount - 1)
  }

  function handleDepartItem(id) {
    setDepartedIds(prevIds => [...prevIds, id])

    changeCount(prevCount => prevCount - 1)
  }

  function handleSettleDepartedItem({ id, isChanged }) {
    setDepartedIds(prevIds => removeRequestBoardId({ id, ids: prevIds }))

    if (isChanged) {
      mutateColumnItems(prevItems =>
        removeRequestBoardColumnItem({ id, items: prevItems })
      )
    } else {
      changeCount(prevCount => prevCount + 1)
    }
  }

  function handleSyncItem({ id, item }) {
    if (loadPermission) {
      setDepartedIds(prevIds => removeRequestBoardId({ id, ids: prevIds }))

      changeCount(
        prevCount =>
          prevCount +
          getRequestBoardColumnSyncCountChange({
            id,
            item,
            items: items ?? EMPTY_LIST,
            status,
          })
      )

      mutateColumnItems(prevItems =>
        syncRequestBoardColumnItem({ id, item, items: prevItems, status })
      )

      loadCount()
    }
  }

  function handleUpdateItem(item) {
    if (loadPermission && item) {
      mutateItems(prevItems =>
        updateRequestBoardColumnItem({ item, items: prevItems, status })
      )
    }
  }

  return {
    count: isExcluded ? REQUEST_BOARD_COLUMN_INITIAL_COUNT : count,
    departedIds,
    getThresholdCardProps,
    handleDepartItem,
    handleInsertItem,
    handleReload,
    handleReloadCount,
    handleRemoveItem,
    handleSettleDepartedItem,
    handleSettleInsertedItem,
    handleSyncItem,
    handleUpdateItem,
    hasCountError: hasCountError && !isExcluded,
    hasError: hasError && !isExcluded,
    insertedIds,
    isCountLoading:
      !isExcluded &&
      !isRequestBoardColumnCountCurrent({ countFilters, filters }),
    isExcluded,
    isLoading: !isExcluded && (loadingInitial || !isEnabled),
    isLoadingMore: loadingMore,
    items: isExcluded
      ? EMPTY_LIST
      : getRequestBoardColumnVisibleItems({
          departedIds,
          items: items ?? EMPTY_LIST,
        }),
    pageGenerationRef,
    reloadGenerationRef,
    status,
  }
}
